import { ActionIcon, Button, ButtonGroup, Menu, Radio, Slider, Switch, Tooltip } from '@mantine/core';
import { sizes, variants } from '@uag/react-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import {
    DIMENSION_UNITS,
    type DimensionAnnotationMode,
    type DimensionChainSettings,
    type DimensionDisplayMode
} from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionSettings';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const displayModeLabelKeys = {
    projection: 'labelDimensionViewProjection',
    overlay: 'labelDimensionViewOverlay'
} as const satisfies Record<DimensionDisplayMode, string>;

const annotationModeLabelKeys = {
    labels: 'labelDimensionAnnotationLabels',
    chains: 'labelDimensionAnnotationChains',
    both: 'labelDimensionAnnotationBoth'
} as const satisfies Record<DimensionAnnotationMode, string>;

const chainLabelKeys = {
    panels: 'labelDimensionChainPanels',
    anchors: 'labelDimensionChainAnchors',
    overall: 'labelDimensionChainOverall',
    heights: 'labelDimensionChainHeights'
} as const satisfies Record<keyof DimensionChainSettings, string>;

export const DimensionOptions = () => {
    const { t } = useTranslation();
    const [menuOpened, setMenuOpened] = useState<boolean>(false);

    const { dimensionSettings, setDimensionSettings } = usePlacementStore(
        useShallow((state) => ({
            dimensionSettings: state.dimensionSettings,
            setDimensionSettings: state.setDimensionSettings
        }))
    );

    const { visible, displayMode, annotationMode, unit, showConnectors, showAnchors, projectionVerticalScale, chains } =
        dimensionSettings;
    const chainsVisible = annotationMode !== 'labels';
    const toggleLabel = visible ? t('labelDisableDimensions') : t('labelEnableDimensions');

    return (
        <div className="flex shrink-0 items-center justify-center px-1 sm:px-1">
            <span className="scene-status-action-pair">
                <Tooltip label={toggleLabel} position="bottom" variant={variants.tooltip.bottom}>
                    <ActionIcon
                        aria-label={toggleLabel}
                        className="bg-background-transparent hover:bg-background-gray! pointer-events-auto action-icon-medium scene-status-action-button-paired-left"
                        variant={visible ? variants.actionIcon.activated : undefined}
                        onPointerDown={() => setDimensionSettings({ visible: !visible })}
                    >
                        straighten
                    </ActionIcon>
                </Tooltip>

                <Menu
                    closeOnItemClick={false}
                    offset={4}
                    opened={menuOpened}
                    position="top-start"
                    shadow="md"
                    withinPortal
                    onChange={setMenuOpened}
                >
                    <Menu.Target>
                        <Tooltip label={t('labelDimensionOptions')} position="bottom" variant={variants.tooltip.bottom}>
                            <ActionIcon
                                aria-label={t('labelDimensionOptions')}
                                className="bg-background-transparent hover:bg-background-gray! pointer-events-auto action-icon-medium scene-status-action-button-compact scene-status-action-button-paired-right leading-none"
                            >
                                {menuOpened ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                            </ActionIcon>
                        </Tooltip>
                    </Menu.Target>

                    <Menu.Dropdown>
                        <Menu.Label className="min-w-60">{t('labelDimensionOptions')}</Menu.Label>
                        <Menu.Divider />

                        <span className="m-2">
                            <Switch
                                checked={visible}
                                label={t('labelShowDimensions')}
                                size="xs"
                                onChange={() => setDimensionSettings({ visible: !visible })}
                            />

                            <div className="text-xs font-medium text-text-light uppercase mt-5">
                                {t('labelDimensionView')}
                            </div>
                            <div className="pl-2">
                                <Radio.Group
                                    value={displayMode}
                                    onChange={(value) =>
                                        setDimensionSettings({ displayMode: value as DimensionDisplayMode })
                                    }
                                >
                                    {Object.entries(displayModeLabelKeys).map(([value, labelKey]) => (
                                        <Radio key={value} label={t(labelKey)} value={value} />
                                    ))}
                                </Radio.Group>
                            </div>

                            {displayMode === 'projection' && (
                                <>
                                    <div className="text-xs font-medium text-text-light uppercase mt-5">
                                        {t('labelDimensionVerticalScale')}
                                    </div>
                                    <div className="mt-2">
                                        <Slider
                                            label={(value) => `${value}%`}
                                            max={100}
                                            min={10}
                                            step={5}
                                            thumbLabel={t('labelDimensionVerticalScale')}
                                            value={Math.round(projectionVerticalScale * 100)}
                                            onChange={(value) =>
                                                setDimensionSettings({ projectionVerticalScale: value / 100 })
                                            }
                                        />
                                    </div>
                                </>
                            )}

                            <div className="text-xs font-medium text-text-light uppercase mt-5">
                                {t('labelDimensionAnnotation')}
                            </div>
                            <div className="pl-2">
                                <Radio.Group
                                    value={annotationMode}
                                    onChange={(value) =>
                                        setDimensionSettings({ annotationMode: value as DimensionAnnotationMode })
                                    }
                                >
                                    {Object.entries(annotationModeLabelKeys).map(([value, labelKey]) => (
                                        <Radio key={value} label={t(labelKey)} value={value} />
                                    ))}
                                </Radio.Group>
                            </div>

                            <div className="text-xs font-medium text-text-light uppercase mt-5">
                                {t('labelDimensionUnit')}
                            </div>
                            <ButtonGroup className="mt-1 w-full" variant={variants.buttonGroup.small}>
                                {DIMENSION_UNITS.map((value) => (
                                    <Button
                                        key={value}
                                        className="flex-1 justify-center px-1 data-[selected=true]:bg-background-action-hover data-[selected=false]:bg-background-default"
                                        classNames={{
                                            inner: 'w-full justify-center',
                                            label: 'w-full justify-center text-center'
                                        }}
                                        data-selected={unit === value}
                                        size={sizes.button.small}
                                        variant={variants.button.secondary}
                                        onClick={() => setDimensionSettings({ unit: value })}
                                    >
                                        {value}
                                    </Button>
                                ))}
                            </ButtonGroup>

                            {chainsVisible && (
                                <>
                                    <div className="text-xs font-medium text-text-light uppercase mt-5">
                                        {t('labelDimensionChains')}
                                    </div>
                                    <div className="pl-2 flex flex-col gap-1">
                                        {(Object.keys(chainLabelKeys) as (keyof DimensionChainSettings)[]).map(
                                            (key) => (
                                                <Switch
                                                    key={key}
                                                    checked={chains[key]}
                                                    label={t(chainLabelKeys[key])}
                                                    size="xs"
                                                    onChange={() =>
                                                        setDimensionSettings({ chains: { [key]: !chains[key] } })
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                </>
                            )}

                            <div className="text-xs font-medium text-text-light uppercase mt-5">
                                {t('labelDetails')}
                            </div>
                            <Switch
                                checked={showConnectors}
                                label={t('labelDimensionShowConnectors')}
                                size="xs"
                                onChange={() => setDimensionSettings({ showConnectors: !showConnectors })}
                            />
                            <Switch
                                checked={showAnchors}
                                label={t('labelDimensionShowAnchors')}
                                size="xs"
                                onChange={() => setDimensionSettings({ showAnchors: !showAnchors })}
                            />
                        </span>
                    </Menu.Dropdown>
                </Menu>
            </span>
        </div>
    );
};
